import { useState, useCallback, useRef, useEffect } from 'react';
import isEqual from 'lodash-es/isEqual'; // Import isEqual for deep comparison

interface UndoRedoState<T> {
  past: T[];
  present: T;
  future: T[];
}

export function useGenericUndoRedo<T>(initialState: T, maxHistory: number = 50) {
  const [state, setState] = useState<UndoRedoState<T>>({
    past: [],
    present: initialState,
    future: [],
  });

  const isUpdatingRef = useRef(false); // To prevent snapshotting during undo/redo

  const takeSnapshot = useCallback((newState: T) => {
    if (isUpdatingRef.current) {
      return; // Do not take snapshot if currently undoing/redoing
    }

    setState(prevState => {
      // Use deep comparison to ensure snapshot is only taken if state meaningfully changes
      if (isEqual(prevState.present, newState)) {
        return prevState;
      }

      const newPast = [...prevState.past, prevState.present];
      if (newPast.length > maxHistory) {
        newPast.shift(); // Remove the oldest snapshot
      }

      return {
        past: newPast,
        present: newState,
        future: [], // Clear future when a new snapshot is taken
      };
    });
  }, [maxHistory]);

  const undo = useCallback(() => {
    setState(prevState => {
      if (prevState.past.length === 0) {
        return prevState;
      }
      isUpdatingRef.current = true;
      const newPast = [...prevState.past];
      const newPresent = newPast.pop()!;
      const newFuture = [prevState.present, ...prevState.future];
      return {
        past: newPast,
        present: newPresent,
        future: newFuture,
      };
    });
  }, []);

  const redo = useCallback(() => {
    setState(prevState => {
      if (prevState.future.length === 0) {
        return prevState;
      }
      isUpdatingRef.current = true;
      const newFuture = [...prevState.future];
      const newPresent = newFuture.shift()!;
      const newPast = [...prevState.past, prevState.present];
      return {
        past: newPast,
        present: newPresent,
        future: newFuture,
      };
    });
  }, []);

  // Reset isUpdatingRef after state update cycle
  useEffect(() => {
    if (isUpdatingRef.current) {
      const timeoutId = setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0); // Small delay to allow React to finish its update cycle
      return () => clearTimeout(timeoutId);
    }
  }, [state]);

  return {
    state: state.present,
    takeSnapshot,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
}
